import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase, getTenantUsersKey } from '../shared.jsx';
import { ConflictExportButton } from './ConflictExport.jsx';

function AdminConflictReport({ user, conflicts, onBack, onSelectConflict }) {
  const toast = useToast();
  const [allUsers, setAllUsers] = useState([]);
  
  // Interactive Report State
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [columnFilters, setColumnFilters] = useState({});
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [activeFilterCol, setActiveFilterCol] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [highlightRules, setHighlightRules] = useState([
    { id: 'overdue', enabled: true, label: 'Overdue deadline', column: 'needByDate', condition: 'overdue', bgColor: 'bg-red-50', textColor: 'text-red-700' },
    { id: 'urgent', enabled: true, label: 'High/Critical urgency', column: 'urgency', condition: 'gte', value: 4, bgColor: 'bg-amber-50', textColor: 'text-amber-700' },
    { id: 'resolved', enabled: false, label: 'Resolved conflicts', column: 'status', condition: 'eq', value: 'resolved', bgColor: 'bg-green-50', textColor: 'text-green-700' },
  ]);
  const [showHighlightPanel, setShowHighlightPanel] = useState(false);
  const [groupByColumn, setGroupByColumn] = useState(null);
  const [showGroupPanel, setShowGroupPanel] = useState(false);
  const [showAggPanel, setShowAggPanel] = useState(false);
  const [aggregations, setAggregations] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState(0);
  const [viewMode, setViewMode] = useState('report'); // 'report' | 'detail' | 'chart'
  const [chartType, setChartType] = useState('status'); // 'status' | 'urgency' | 'timeline' | 'phase'
  const tableRef = React.useRef(null);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const tenantId = user?.tenantId || 'tenant-hci';
        const users = await storage.get(getTenantUsersKey(tenantId), true) || [];
        setAllUsers(Array.isArray(users) ? users : []);
      } catch (e) { /* ignore */ }
    };
    loadUsers();
  }, []);

  // Column definitions
  const allColumns = [
    { key: 'title', label: 'Title', type: 'text', width: 'min-w-[200px]', sticky: true },
    { key: 'status', label: 'Status', type: 'status', width: 'min-w-[140px]' },
    { key: 'urgency', label: 'Urgency', type: 'urgency', width: 'min-w-[100px]' },
    { key: 'createdAt', label: 'Created', type: 'date', width: 'min-w-[120px]' },
    { key: 'updatedAt', label: 'Last Updated', type: 'date', width: 'min-w-[120px]' },
    { key: 'needByDate', label: 'Need By', type: 'date', width: 'min-w-[120px]' },
    { key: 'creator', label: 'Creator', type: 'text', width: 'min-w-[130px]' },
    { key: 'menteeCount', label: 'Participants', type: 'number', width: 'min-w-[80px]' },
    { key: 'menteeNames', label: 'Participants', type: 'text', width: 'min-w-[180px]' },
    { key: 'mentor', label: 'Mentor', type: 'text', width: 'min-w-[120px]' },
    { key: 'responseHours', label: 'Resp. Hours', type: 'text', width: 'min-w-[100px]' },
    { key: 'responseWindow', label: 'Resp. Window', type: 'number', width: 'min-w-[100px]' },
    { key: 'maxCommunicationCycles', label: 'Max Cycles', type: 'number', width: 'min-w-[90px]' },
    { key: 'currentPhase', label: 'Current Phase', type: 'text', width: 'min-w-[140px]' },
    { key: 'commCyclesUsed', label: 'Cycles Used', type: 'number', width: 'min-w-[90px]' },
    { key: 'alternativesCount', label: 'Alternatives', type: 'number', width: 'min-w-[90px]' },
    { key: 'workingHours', label: 'Working Hours', type: 'number', width: 'min-w-[110px]' },
    { key: 'auditCount', label: 'Audit Entries', type: 'number', width: 'min-w-[100px]' },
    { key: 'model', label: 'Model', type: 'text', width: 'min-w-[100px]' },
  ];

  const visibleColumns = allColumns.filter(c => !hiddenColumns.has(c.key));

  // Flatten conflict data for the table
  const flattenConflict = (c) => {
    const creatorMentee = c.mentees?.find(m => m.isCreator || m.id === c.createdBy);
    const creatorUser = allUsers.find(u => u.id === c.createdBy);
    const creatorName = creatorMentee?.name || creatorUser?.name || 'Unknown';
    const menteeNames = (c.mentees || []).map(m => m.name).join(', ');
    const mentorName = c.mentor?.name || '—';
    const cycles = c.steps?.communicate?.cycles || [];
    const alts = c.steps?.exploreAlternatives?.alternatives || [];
    
    const phaseMap = {
      'pending-acceptance': 'Pending Acceptance',
      'changes-proposed': 'Changes Proposed',
      'identify-define': 'Identify & Define',
      'communicate': 'Communicate',
      'explore-alternatives': 'Explore Alternatives',
      'evaluate-select': 'Evaluate & Select',
      'agree-implement': 'Agree & Implement',
      'follow-up': 'Follow-up',
      'resolved': 'Resolved'
    };

    let workingHrs = 0;
    if (c.status === 'resolved') {
      const resolved = c.statusHistory?.find(h => h.status === 'resolved');
      if (resolved) workingHrs = calculateWorkingHours(c.createdAt, resolved.timestamp, c.responseHours);
    } else {
      workingHrs = calculateWorkingHours(c.createdAt, new Date().toISOString(), c.responseHours);
    }

    return {
      _raw: c,
      id: c.id,
      title: c.title || 'Untitled',
      status: c.status || 'unknown',
      urgency: c.urgency || 3,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
      needByDate: c.needByDate || null,
      creator: creatorName,
      menteeCount: c.mentees?.length || 0,
      menteeNames,
      mentor: mentorName,
      responseHours: c.responseHours || '24',
      responseWindow: c.responseWindow || 15,
      maxCommunicationCycles: c.maxCommunicationCycles || 5,
      currentPhase: phaseMap[c.status] || c.status,
      commCyclesUsed: Math.ceil(cycles.length / 2),
      alternativesCount: alts.length,
      workingHours: parseFloat(workingHrs.toFixed(1)),
      auditCount: c.auditLog?.length || 0,
      model: c.model || '—',
    };
  };

  const flatData = conflicts.map(flattenConflict);

  // Search filter
  const searchFiltered = flatData.filter(row => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return row.title.toLowerCase().includes(term) ||
      row.creator.toLowerCase().includes(term) ||
      row.menteeNames.toLowerCase().includes(term) ||
      row.mentor.toLowerCase().includes(term) ||
      row.status.toLowerCase().includes(term) ||
      row.currentPhase.toLowerCase().includes(term);
  });

  // Column filters
  const columnFiltered = searchFiltered.filter(row => {
    for (const [col, filterVal] of Object.entries(columnFilters)) {
      if (!filterVal || filterVal === '') continue;
      const cellVal = String(row[col] ?? '').toLowerCase();
      if (!cellVal.includes(filterVal.toLowerCase())) return false;
    }
    return true;
  });

  // Sorting
  const sorted = [...columnFiltered].sort((a, b) => {
    const aVal = a[sortColumn];
    const bVal = b[sortColumn];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    let cmp = 0;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      cmp = aVal - bVal;
    } else {
      cmp = String(aVal).localeCompare(String(bVal));
    }
    return sortDirection === 'asc' ? cmp : -cmp;
  });

  // Grouping
  const grouped = groupByColumn ? (() => {
    const groups = {};
    sorted.forEach(row => {
      const key = String(row[groupByColumn] ?? '(empty)');
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });
    return groups;
  })() : null;

  // Pagination
  const totalRows = sorted.length;
  const totalPages = Math.ceil(totalRows / pageSize);
  const paginatedRows = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Aggregations
  const computeAggregation = (col, type) => {
    const numericCols = allColumns.filter(c => c.type === 'number').map(c => c.key);
    if (!numericCols.includes(col)) return '—';
    const vals = sorted.map(r => r[col]).filter(v => v != null && !isNaN(v));
    if (vals.length === 0) return '—';
    switch (type) {
      case 'sum': return vals.reduce((s, v) => s + v, 0).toFixed(1);
      case 'avg': return (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1);
      case 'min': return Math.min(...vals).toFixed(1);
      case 'max': return Math.max(...vals).toFixed(1);
      case 'count': return vals.length;
      default: return '—';
    }
  };

  // Highlight check
  const getRowHighlight = (row) => {
    for (const rule of highlightRules) {
      if (!rule.enabled) continue;
      const val = row[rule.column];
      let match = false;
      if (rule.condition === 'overdue') {
        match = val && new Date(val) < new Date();
      } else if (rule.condition === 'gte') {
        match = val >= rule.value;
      } else if (rule.condition === 'eq') {
        match = val === rule.value;
      }
      if (match) return rule;
    }
    return null;
  };

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  // CSV Download
  const handleDownloadCSV = () => {
    const headers = visibleColumns.map(c => c.label);
    const rows = sorted.map(row => visibleColumns.map(c => {
      const val = row[c.key];
      if (c.type === 'date' && val) return new Date(val).toLocaleString();
      return String(val ?? '');
    }));
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conflict-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast('CSV downloaded');
  };

  // Reset all
  const handleReset = () => {
    setSearchTerm('');
    setSortColumn('updatedAt');
    setSortDirection('desc');
    setColumnFilters({});
    setHiddenColumns(new Set());
    setGroupByColumn(null);
    setAggregations([]);
    setCurrentPage(1);
    setExpandedRow(null);
    toast('Report reset to defaults');
  };

  const statusColors = {
    'pending-acceptance': { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
    'changes-proposed': { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500' },
    'identify-define': { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' },
    'communicate': { bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500' },
    'explore-alternatives': { bg: 'bg-indigo-100', text: 'text-indigo-800', dot: 'bg-indigo-500' },
    'evaluate-select': { bg: 'bg-cyan-100', text: 'text-cyan-800', dot: 'bg-cyan-500' },
    'agree-implement': { bg: 'bg-teal-100', text: 'text-teal-800', dot: 'bg-teal-500' },
    'follow-up': { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500' },
    'resolved': { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
  };

  const urgencyLabels = { 1: 'Low', 2: 'Minor', 3: 'Moderate', 4: 'High', 5: 'Critical' };
  const urgencyColors = { 1: 'bg-blue-100 text-blue-700', 2: 'bg-blue-100 text-blue-700', 3: 'bg-amber-100 text-amber-700', 4: 'bg-red-100 text-red-700', 5: 'bg-red-200 text-red-800' };

  const renderCell = (row, col) => {
    const val = row[col.key];
    if (col.type === 'status') {
      const sc = statusColors[val] || { bg: 'bg-stone-100', text: 'text-stone-700', dot: 'bg-stone-400' };
      return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${sc.bg} ${sc.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
          {(val || 'unknown').replace(/-/g, ' ')}
        </span>
      );
    }
    if (col.type === 'urgency') {
      return (
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${urgencyColors[val] || 'bg-stone-100 text-stone-700'}`}>
          {val}/5 {urgencyLabels[val] || ''}
        </span>
      );
    }
    if (col.type === 'date') {
      if (!val) return <span className="text-stone-300">—</span>;
      const d = new Date(val);
      const isOverdue = col.key === 'needByDate' && d < new Date();
      return <span className={isOverdue ? 'text-red-600 font-medium' : ''}>{d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' })}</span>;
    }
    if (col.type === 'number') {
      return <span className="font-mono text-sm">{val ?? '—'}</span>;
    }
    if (col.key === 'title') {
      return (
        <button onClick={(e) => { e.stopPropagation(); onSelectConflict && onSelectConflict(row._raw); }}
          className="text-left text-indigo-700 hover:text-indigo-900 hover:underline font-medium truncate max-w-[280px] block">
          {val}
        </button>
      );
    }
    return <span className="truncate block max-w-[200px]">{val ?? '—'}</span>;
  };

  // Detail view for expanded row
  const renderDetailView = (row) => {
    const c = row._raw;
    const identifyData = c.steps?.identifyDefine?.data || {};
    const cycles = c.steps?.communicate?.cycles || [];
    const alts = c.steps?.exploreAlternatives?.alternatives || [];
    const ratings = c.steps?.evaluateSelect?.ratings || {};
    const followUpRatings = c.steps?.followUp?.ratings || {};
    const auditLog = c.auditLog || [];

    return (
      <tr>
        <td colSpan={visibleColumns.length} className="p-0">
          <div className="bg-indigo-50/50 border-y border-indigo-200 p-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Problem Statement */}
              <div className="bg-white rounded-lg border border-stone-200 p-4">
                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Problem Statement</h4>
                <div className="space-y-1.5 text-sm text-stone-700">
                  {c.problemStatement?.who && <div><span className="text-stone-500 text-xs">Who:</span> {c.problemStatement.who}</div>}
                  {c.problemStatement?.what && <div><span className="text-stone-500 text-xs">What:</span> {c.problemStatement.what}</div>}
                  {c.problemStatement?.where && <div><span className="text-stone-500 text-xs">Where:</span> {c.problemStatement.where}</div>}
                  {c.problemStatement?.when && <div><span className="text-stone-500 text-xs">When:</span> {c.problemStatement.when}</div>}
                  {c.problemStatement?.how && <div><span className="text-stone-500 text-xs">How:</span> {c.problemStatement.how}</div>}
                </div>
                {c.desiredOutcome && (
                  <div className="mt-3 pt-3 border-t border-stone-100">
                    <span className="text-stone-500 text-xs">Desired Outcome:</span>
                    <p className="text-sm text-stone-700">{c.desiredOutcome}</p>
                  </div>
                )}
              </div>

              {/* Participants */}
              <div className="bg-white rounded-lg border border-stone-200 p-4">
                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Participants & Roles</h4>
                <div className="space-y-1.5">
                  {(c.mentees || []).map((m, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                        {(m.name || '?')[0].toUpperCase()}
                      </div>
                      <span className="text-stone-700">{m.name}</span>
                      <span className="text-[10px] text-stone-500">({m.email})</span>
                      {m.isCreator && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Creator</span>}
                    </div>
                  ))}
                  {c.mentor && (
                    <div className="flex items-center gap-2 text-sm pt-1 border-t border-stone-100 mt-1">
                      <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">M</div>
                      <span className="text-stone-700">{c.mentor.name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">Mentor</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Phase Progress */}
              <div className="bg-white rounded-lg border border-stone-200 p-4">
                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Phase Progress</h4>
                <div className="space-y-2">
                  {[
                    { key: 'identifyDefine', label: 'Identify & Define', detail: `${Object.keys(identifyData).length} responses` },
                    { key: 'communicate', label: 'Communicate', detail: `${Math.ceil(cycles.length / 2)}/${c.maxCommunicationCycles || 5} cycles` },
                    { key: 'exploreAlternatives', label: 'Explore Alternatives', detail: `${alts.length} submitted` },
                    { key: 'evaluateSelect', label: 'Evaluate & Select', detail: `${Object.keys(ratings).length} ratings` },
                    { key: 'agreeImplement', label: 'Agree & Implement', detail: `${(c.steps?.agreeImplement?.acknowledged || []).length} acknowledged` },
                    { key: 'followUp', label: 'Follow-up', detail: `${Object.keys(followUpRatings).length} rated` },
                  ].map(phase => {
                    const completed = c.steps?.[phase.key]?.completed;
                    const isCurrent = c.status === phase.key.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '');
                    return (
                      <div key={phase.key} className={`flex items-center gap-3 px-2 py-1.5 rounded ${isCurrent ? 'bg-indigo-50 border border-indigo-200' : ''}`}>
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                          completed ? 'bg-green-500 text-white' : isCurrent ? 'bg-indigo-600 text-white' : 'bg-stone-200 text-stone-500'
                        }`}>
                          {completed ? <CheckCircle className="w-3 h-3" /> : <span className="text-[9px] font-bold">{isCurrent ? '>' : ''}</span>}
                        </div>
                        <span className={`text-sm flex-1 ${completed ? 'text-green-700' : isCurrent ? 'text-indigo-700 font-medium' : 'text-stone-500'}`}>{phase.label}</span>
                        <span className="text-[10px] text-stone-500">{phase.detail}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent Audit Log */}
              <div className="bg-white rounded-lg border border-stone-200 p-4">
                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Recent Activity ({auditLog.length} total)</h4>
                {auditLog.length === 0 ? (
                  <p className="text-sm text-stone-500">No audit entries</p>
                ) : (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {auditLog.slice(-8).reverse().map((entry, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-[10px] text-stone-500 whitespace-nowrap mt-0.5">
                          {new Date(entry.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </span>
                        <span className="text-stone-600">{entry.userName}: {entry.action}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end mt-4 pt-3 border-t border-indigo-200">
              <ConflictExportButton conflict={c} compact={true} />
            </div>
          </div>
        </td>
      </tr>
    );
  };

  // Chart view
  const renderChartView = () => {
    const getChartData = () => {
      if (chartType === 'status') {
        const counts = {};
        sorted.forEach(r => { counts[r.status] = (counts[r.status] || 0) + 1; });
        return Object.entries(counts).map(([k, v]) => ({ label: k.replace(/-/g, ' '), value: v, color: statusColors[k]?.dot || 'bg-stone-400' }));
      }
      if (chartType === 'urgency') {
        const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        sorted.forEach(r => { counts[r.urgency] = (counts[r.urgency] || 0) + 1; });
        return Object.entries(counts).map(([k, v]) => ({ label: `${urgencyLabels[k]} (${k})`, value: v, color: k >= 4 ? 'bg-red-500' : k == 3 ? 'bg-amber-500' : 'bg-blue-500' }));
      }
      if (chartType === 'phase') {
        const counts = {};
        sorted.forEach(r => { counts[r.currentPhase] = (counts[r.currentPhase] || 0) + 1; });
        return Object.entries(counts).map(([k, v]) => ({ label: k, value: v, color: 'bg-indigo-500' }));
      }
      if (chartType === 'timeline') {
        const months = {};
        sorted.forEach(r => {
          const m = new Date(r.createdAt).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
          months[m] = (months[m] || 0) + 1;
        });
        return Object.entries(months).map(([k, v]) => ({ label: k, value: v, color: 'bg-indigo-500' }));
      }
      return [];
    };

    const data = getChartData();
    const maxVal = Math.max(...data.map(d => d.value), 1);

    return (
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          {[
            { key: 'status', label: 'By Status' },
            { key: 'urgency', label: 'By Urgency' },
            { key: 'phase', label: 'By Phase' },
            { key: 'timeline', label: 'Timeline' },
          ].map(ct => (
            <button key={ct.key} onClick={() => setChartType(ct.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${chartType === ct.key ? 'bg-indigo-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
              {ct.label}
            </button>
          ))}
        </div>
        <div className="space-y-2">
          {data.map((d, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-xs text-stone-600 w-32 truncate text-right">{d.label}</span>
              <div className="flex-1 h-7 bg-stone-100 rounded-full overflow-hidden">
                <div className={`h-full ${d.color} rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
                  style={{ width: `${Math.max(2, (d.value / maxVal) * 100)}%` }}>
                  <span className="text-[11px] font-bold text-white drop-shadow-sm">{d.value}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-stone-100 text-xs text-stone-500">
          {sorted.length} conflict{sorted.length !== 1 ? 's' : ''} in report
        </div>
      </div>
    );
  };

  // Group rendering
  const renderGroupedRows = () => {
    if (!grouped) return null;
    const groupCol = allColumns.find(c => c.key === groupByColumn);
    return Object.entries(grouped).map(([groupKey, rows]) => (
      <React.Fragment key={groupKey}>
        <tr className="bg-indigo-50">
          <td colSpan={visibleColumns.length} className="px-4 py-2">
            <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">{groupCol?.label || groupByColumn}: </span>
            <span className="text-sm font-medium text-indigo-900">{groupKey}</span>
            <span className="text-xs text-indigo-600 ml-2">({rows.length} record{rows.length !== 1 ? 's' : ''})</span>
          </td>
        </tr>
        {rows.map(row => renderDataRow(row))}
      </React.Fragment>
    ));
  };

  const renderDataRow = (row) => {
    const highlight = getRowHighlight(row);
    const isExpanded = expandedRow === row.id;
    return (
      <React.Fragment key={row.id}>
        <tr onClick={() => setExpandedRow(isExpanded ? null : row.id)}
          className={`border-b border-stone-100 cursor-pointer transition-colors ${
            highlight ? `${highlight.bgColor}` : 'hover:bg-stone-50'
          } ${isExpanded ? 'bg-indigo-50/30' : ''}`}>
          {visibleColumns.map((col, colIndex) => (
            <td key={col.key} className={`px-3 py-2.5 text-sm ${highlight ? highlight.textColor : 'text-stone-700'} ${col.width}`}>
              {colIndex === 0 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setExpandedRow(isExpanded ? null : row.id); }}
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? 'Hide record details' : 'Show record details'}
                  className="inline-flex align-middle mr-1 p-0.5 rounded hover:bg-stone-100 text-stone-600"
                >
                  {isExpanded ? <ChevronDown className="w-4 h-4" aria-hidden="true" /> : <ChevronRight className="w-4 h-4" aria-hidden="true" />}
                </button>
              )}
              {renderCell(row, col)}
            </td>
          ))}
        </tr>
        {isExpanded && renderDetailView(row)}
      </React.Fragment>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-stone-100">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 shadow-sm px-3 sm:px-6 py-4">
        <div className="max-w-full mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-stone-100 rounded-lg transition-colors" aria-label="Back">
              <ArrowLeft className="w-5 h-5 text-stone-600" aria-hidden="true" />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-stone-800">Conflict Report</h1>
              <p className="text-xs text-stone-500">{sorted.length} of {conflicts.length} conflict{conflicts.length !== 1 ? 's' : ''}{searchTerm || Object.keys(columnFilters).length > 0 ? ' (filtered)' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleReset} className="px-3 py-1.5 text-xs text-stone-600 hover:text-stone-700 hover:bg-stone-100 rounded-lg transition-colors">Reset</button>
            <button onClick={handleDownloadCSV} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-lg font-medium transition-colors">
              <Download className="w-3.5 h-3.5" />
              CSV
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-full mx-auto px-3 sm:px-6 py-4">
        {/* Toolbar */}
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm mb-4 p-3">
          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-stone-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="text" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                placeholder="Search all columns..."
                className="w-full pl-9 pr-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white" />
            </div>

            {/* View Mode */}
            <div className="flex bg-stone-100 rounded-lg p-0.5">
              {[
                { key: 'report', icon: <FileText className="w-3.5 h-3.5" /> },
                { key: 'chart', icon: <BarChart3 className="w-3.5 h-3.5" /> },
              ].map(vm => (
                <button key={vm.key} onClick={() => setViewMode(vm.key)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    viewMode === vm.key ? 'bg-white text-indigo-700 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                  }`}>
                  {vm.icon}
                  <span className="hidden sm:inline capitalize">{vm.key}</span>
                </button>
              ))}
            </div>

            {/* Action buttons */}
            <button onClick={() => setShowColumnPicker(!showColumnPicker)}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-colors ${showColumnPicker ? 'bg-indigo-100 text-indigo-700' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
              <Columns className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Columns</span>
            </button>
            <button onClick={() => setShowFilterPanel(!showFilterPanel)}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-colors ${showFilterPanel ? 'bg-indigo-100 text-indigo-700' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
              <Filter className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Filter</span>
              {Object.values(columnFilters).filter(v => v).length > 0 && (
                <span className="w-4 h-4 bg-indigo-600 text-white rounded-full text-[10px] flex items-center justify-center">{Object.values(columnFilters).filter(v => v).length}</span>
              )}
            </button>
            <button onClick={() => setShowHighlightPanel(!showHighlightPanel)}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-colors ${showHighlightPanel ? 'bg-indigo-100 text-indigo-700' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
              <Eye className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Highlight</span>
            </button>
            <button onClick={() => setShowGroupPanel(!showGroupPanel)}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-colors ${showGroupPanel ? 'bg-indigo-100 text-indigo-700' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
              <Maximize2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Group</span>
            </button>
            <button onClick={() => setShowAggPanel(!showAggPanel)}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-colors ${showAggPanel ? 'bg-indigo-100 text-indigo-700' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
              <BarChart3 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Aggregate</span>
            </button>

            {/* Rows per page */}
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              className="px-2 py-1.5 text-xs bg-stone-100 border-0 rounded-lg text-stone-600 focus:outline-none focus:ring-2 focus:ring-indigo-400">
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} rows</option>)}
            </select>
          </div>

          {/* Column Picker Panel */}
          {showColumnPicker && (
            <div className="mt-3 pt-3 border-t border-stone-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Show/Hide Columns</span>
                <div className="flex gap-2">
                  <button onClick={() => setHiddenColumns(new Set())} className="text-[10px] text-indigo-600 hover:underline">Show All</button>
                  <button onClick={() => setHiddenColumns(new Set(allColumns.slice(5).map(c => c.key)))} className="text-[10px] text-indigo-600 hover:underline">Minimal</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {allColumns.map(col => (
                  <button key={col.key}
                    onClick={() => {
                      const next = new Set(hiddenColumns);
                      next.has(col.key) ? next.delete(col.key) : next.add(col.key);
                      setHiddenColumns(next);
                    }}
                    className={`px-2.5 py-1 text-[11px] rounded-full transition-colors ${
                      !hiddenColumns.has(col.key) ? 'bg-indigo-100 text-indigo-700 font-medium' : 'bg-stone-100 text-stone-600'
                    }`}>
                    {col.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Filter Panel */}
          {showFilterPanel && (
            <div className="mt-3 pt-3 border-t border-stone-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Column Filters</span>
                <button onClick={() => setColumnFilters({})} className="text-[10px] text-indigo-600 hover:underline">Clear All</button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {visibleColumns.map(col => (
                  <div key={col.key}>
                    <label className="text-[10px] text-stone-500 block mb-0.5">{col.label}</label>
                    {col.key === 'status' ? (
                      <select value={columnFilters[col.key] || ''} onChange={e => { setColumnFilters({ ...columnFilters, [col.key]: e.target.value }); setCurrentPage(1); }}
                        className="w-full px-2 py-1.5 text-xs bg-stone-50 border border-stone-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400">
                        <option value="">All</option>
                        {['pending-acceptance', 'identify-define', 'communicate', 'explore-alternatives', 'evaluate-select', 'agree-implement', 'follow-up', 'resolved'].map(s => (
                          <option key={s} value={s}>{s.replace(/-/g, ' ')}</option>
                        ))}
                      </select>
                    ) : col.key === 'urgency' ? (
                      <select value={columnFilters[col.key] || ''} onChange={e => { setColumnFilters({ ...columnFilters, [col.key]: e.target.value }); setCurrentPage(1); }}
                        className="w-full px-2 py-1.5 text-xs bg-stone-50 border border-stone-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400">
                        <option value="">All</option>
                        {[1,2,3,4,5].map(u => <option key={u} value={u}>{urgencyLabels[u]} ({u})</option>)}
                      </select>
                    ) : (
                      <input type="text" value={columnFilters[col.key] || ''} onChange={e => { setColumnFilters({ ...columnFilters, [col.key]: e.target.value }); setCurrentPage(1); }}
                        placeholder="contains..."
                        className="w-full px-2 py-1.5 text-xs bg-stone-50 border border-stone-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Highlight Panel */}
          {showHighlightPanel && (
            <div className="mt-3 pt-3 border-t border-stone-100">
              <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider block mb-2">Highlight Rules</span>
              <div className="space-y-1.5">
                {highlightRules.map(rule => (
                  <div key={rule.id} className="flex items-center gap-3">
                    <button onClick={() => setHighlightRules(highlightRules.map(r => r.id === rule.id ? { ...r, enabled: !r.enabled } : r))}
                      className={`w-8 h-5 rounded-full transition-colors relative ${rule.enabled ? 'bg-indigo-600' : 'bg-stone-300'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${rule.enabled ? 'left-3.5' : 'left-0.5'}`}></span>
                    </button>
                    <span className={`flex-1 text-xs ${rule.enabled ? 'text-stone-700' : 'text-stone-500'}`}>{rule.label}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] ${rule.bgColor} ${rule.textColor}`}>Preview</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Group Panel */}
          {showGroupPanel && (
            <div className="mt-3 pt-3 border-t border-stone-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Group By</span>
                {groupByColumn && <button onClick={() => setGroupByColumn(null)} className="text-[10px] text-indigo-600 hover:underline">Clear</button>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['status', 'urgency', 'creator', 'mentor', 'currentPhase', 'model', 'responseHours'].map(col => (
                  <button key={col} onClick={() => setGroupByColumn(groupByColumn === col ? null : col)}
                    className={`px-2.5 py-1 text-[11px] rounded-full transition-colors ${groupByColumn === col ? 'bg-indigo-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
                    {allColumns.find(c => c.key === col)?.label || col}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Aggregate Panel */}
          {showAggPanel && (
            <div className="mt-3 pt-3 border-t border-stone-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Aggregations</span>
                <button onClick={() => setAggregations([...aggregations, { id: Date.now(), column: 'workingHours', type: 'avg' }])}
                  className="text-[10px] text-indigo-600 hover:underline">+ Add</button>
              </div>
              {aggregations.length === 0 ? (
                <p className="text-xs text-stone-500">Click "+ Add" to add an aggregation</p>
              ) : (
                <div className="space-y-2">
                  {aggregations.map((agg, i) => (
                    <div key={agg.id} className="flex items-center gap-2">
                      <select value={agg.type} onChange={e => setAggregations(aggregations.map((a, j) => j === i ? { ...a, type: e.target.value } : a))}
                        className="px-2 py-1 text-xs bg-stone-50 border border-stone-200 rounded-md">
                        {['sum', 'avg', 'min', 'max', 'count'].map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                      </select>
                      <select value={agg.column} onChange={e => setAggregations(aggregations.map((a, j) => j === i ? { ...a, column: e.target.value } : a))}
                        className="px-2 py-1 text-xs bg-stone-50 border border-stone-200 rounded-md">
                        {allColumns.filter(c => c.type === 'number').map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                      <span className="text-sm font-mono font-medium text-indigo-700 min-w-[60px]">= {computeAggregation(agg.column, agg.type)}</span>
                      <button onClick={() => setAggregations(aggregations.filter((_, j) => j !== i))} className="p-1 text-stone-500 hover:text-red-500" aria-label="Remove aggregation"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Chart View */}
        {viewMode === 'chart' && renderChartView()}

        {/* Report Table */}
        {viewMode === 'report' && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto" ref={tableRef}>
              <table className="w-full">
                <thead>
                  <tr className="bg-stone-50 border-b border-stone-200">
                    {visibleColumns.map(col => (
                      <th key={col.key}
                        onClick={() => handleSort(col.key)}
                        className={`px-3 py-2.5 text-left text-[11px] font-semibold text-stone-600 uppercase tracking-wider cursor-pointer hover:bg-stone-100 select-none transition-colors ${col.width}`}>
                        <div className="flex items-center gap-1">
                          {col.label}
                          {sortColumn === col.key && (
                            <span className="text-indigo-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                          {columnFilters[col.key] && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grouped ? renderGroupedRows() : paginatedRows.map(row => renderDataRow(row))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!grouped && totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-stone-100 bg-stone-50/50">
                <span className="text-xs text-stone-500">
                  Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalRows)} of {totalRows}
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                    className="p-1.5 text-stone-500 hover:text-stone-700 disabled:opacity-30 rounded"><ChevronLeft className="w-4 h-4" /><ChevronLeft className="w-4 h-4 -ml-3" /></button>
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                    className="p-1.5 text-stone-500 hover:text-stone-700 disabled:opacity-30 rounded"><ChevronLeft className="w-4 h-4" /></button>
                  <span className="px-3 py-1 text-xs font-medium text-stone-700">Page {currentPage} of {totalPages}</span>
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                    className="p-1.5 text-stone-500 hover:text-stone-700 disabled:opacity-30 rounded"><ChevronRight className="w-4 h-4" /></button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
                    className="p-1.5 text-stone-500 hover:text-stone-700 disabled:opacity-30 rounded"><ChevronRight className="w-4 h-4" /><ChevronRight className="w-4 h-4 -ml-3" /></button>
                </div>
              </div>
            )}

            {sorted.length === 0 && (
              <div className="p-12 text-center">
                <FileText className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500">No conflicts match your filters</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Dashboard View Component

export default AdminConflictReport;
